import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import { Map as HereMap, Route, SingleMapMarker } from 'components/map/Map';
import type { MapApi, MapLayer, Position } from 'components/map/types';
import { getMapCredentials } from 'configuration';
import { transformToRioPosition } from 'helper/position';
import type { DeviationWithVIN } from 'helper/types';
import useCenterVehicle from 'hooks/useCenterVehicle';
import { memo, type PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDeepEqual } from 'remeda';
import { DEFAULT_LOCALE } from 'store/i18n/lang';
import {
    getFollowSelectedVehicle,
    getIsPVLTriggered,
    getMapLayers,
    getSelectedVehicle,
    getShowHubs,
    setFollowSelectedVehicle,
    setIsFullMissionRouteVisible,
    setMapLayers,
} from 'store/slices/appSlice';

import CustomMapSettings from './CustomMapSettings';
import HubMarker from './HubMarker';
import { useBoundingBox } from './hooks/useBoundingBox';
import { getRouteColor } from './utils';
import VehicleMarker from './VehicleMarker';

import type { Hub, MissionV2, VehicleV2 } from 'API';

// import { useFocusMissionRoute } from './hooks/useFocusMissionRoute'

const mapCredentials = getMapCredentials();

interface MapProps {
    activeMission?: MissionV2 | null;
    hubs?: Hub[];
    vehicles: VehicleV2[];
    deviations?: DeviationWithVIN[];
    selectedVehicle?: VehicleV2;
    center?: Position;
    zoom?: number;
    selectedVehicleState?: any;
}

const Map = ({ hubs, vehicles, selectedVehicle, activeMission, children }: PropsWithChildren<MapProps>) => {
    const mapApiRef = useRef<MapApi | null>(null);
    const mapLayers = useSelector(getMapLayers);
    const showHubs = useSelector(getShowHubs);
    const isVehicleFocused = useSelector(getFollowSelectedVehicle);
    const selectedVehicleState = useSelector(getSelectedVehicle);
    const isPVLTriggered = useSelector(getIsPVLTriggered);
    const selectedVIN = selectedVehicleState?.selectedVIN;

    const dispatch = useDispatch();

    const { boundingBox, focusOnMissionRoute, updateBoundingBox, resetBoundingBox } = useBoundingBox(vehicles);
    const { center, zoom, reset: resetCenterAndZoom } = useCenterVehicle(selectedVehicle);

    const showRoute = useMemo(
        () =>
            !!selectedVehicle?.activeMissionId?.value?.data &&
            !!activeMission?.id &&
            selectedVehicle?.activeMissionId?.value?.data === activeMission?.id,
        [selectedVehicle?.activeMissionId?.value?.data, activeMission?.id]
    );

    const focusOnSelectedVehicle = () => {
        resetBoundingBox();
        dispatch(setFollowSelectedVehicle(!isVehicleFocused));
        dispatch(setIsFullMissionRouteVisible(false));
    };

    const unfollowSelectedVehicle = () => {
        dispatch(setFollowSelectedVehicle(false));
    };

    const showVehicleMissionRoute = () => {
        if (selectedVehicle?.activeMissionId?.value?.data && activeMission && activeMission.route) {
            const missionWaypoints = activeMission.route.positions;
            dispatch(setIsFullMissionRouteVisible(true));
            unfollowSelectedVehicle();
            focusOnMissionRoute(selectedVehicle, missionWaypoints);
        }
    };

    const resetToBoundingBox = () => {
        unfollowSelectedVehicle();
        dispatch(setIsFullMissionRouteVisible(false));
        resetCenterAndZoom();
        updateBoundingBox();
    };

    const eventListenerMap = {
        [EventUtils.DRAG]: unfollowSelectedVehicle,
        [EventUtils.MAP_VIEW_CHANGE_END]: () => {
            const newZoom = mapApiRef.current?.utils?.getZoom();
            if (newZoom === undefined || zoom === undefined) {
                return;
            }
            if (newZoom !== zoom) {
                unfollowSelectedVehicle();
            }
        },
    };

    useEffect(() => {
        if (selectedVIN && activeMission && isPVLTriggered) {
            showVehicleMissionRoute();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVIN, activeMission, isPVLTriggered]);

    /**
     * TODO: to work over zoom feature
     * - reset boundingBox (or add zoom variable)
     * - possibility of working with onZoomIn and onZoomOut events
     */
    return (
        <HereMap
            eventListenerMap={eventListenerMap}
            credentials={mapCredentials}
            boundingBox={boundingBox}
            language={DEFAULT_LOCALE}
            mapLayer={mapLayers}
            enableWebGL
            center={center}
            zoom={zoom}
            showScaleBar
            enableDevicePixelRatio
            onZoomOut={() => unfollowSelectedVehicle()}
            onMapLayerChange={(activeMapLayers: MapLayer[]) => dispatch(setMapLayers(activeMapLayers))}
            // mapSettings={<MapSettings options={getMapSettingsOptions()} />}
            mapSettings={
                <CustomMapSettings
                    selectedVehicle={selectedVehicle}
                    resetToBoundingBox={resetToBoundingBox}
                    unfollowSelectedVehicle={unfollowSelectedVehicle}
                    focusOnSelectedVehicle={focusOnSelectedVehicle}
                    showVehicleMissionRoute={showVehicleMissionRoute}
                />
            }
        >
            {(api: MapApi) => {
                mapApiRef.current = api;
                return (
                    <>
                        {children}
                        {vehicles.map(vehicle => (
                            <VehicleMarker key={vehicle.vin} vehicle={vehicle} />
                        ))}
                        {showHubs && hubs?.map(hub => <HubMarker key={hub.id} hub={hub} />)}
                        {showRoute && (
                            <Route
                                positions={activeMission?.route?.positions.map(transformToRioPosition) || []}
                                startIcon={<SingleMapMarker iconNames={['start']} markerColor='bg-map-marker-route' />}
                                endIcon={<SingleMapMarker iconNames={['finish']} markerColor='bg-map-marker-route' />}
                                hasArrows={false}
                                style={{
                                    width: 5,
                                    borderWidth: 2,
                                    isBorderIncludedInWidth: false,
                                    ...getRouteColor(activeMission?.state?.value?.state),
                                }}
                            />
                        )}
                    </>
                );
            }}
        </HereMap>
    );
};

const arePropsEqual = (prevProps: MapProps, nextProps: MapProps) =>
    isDeepEqual(prevProps.center, nextProps.center) &&
    isDeepEqual(prevProps.zoom, nextProps.zoom) &&
    isDeepEqual(prevProps.hubs, nextProps.hubs) &&
    isDeepEqual(prevProps.selectedVehicleState, nextProps.selectedVehicleState) &&
    isDeepEqual(
        prevProps.vehicles.map(_ => _.position),
        nextProps.vehicles.map(_ => _.position)
    ) &&
    isDeepEqual(prevProps.deviations, nextProps.deviations) &&
    isDeepEqual(prevProps.activeMission, nextProps.activeMission) &&
    prevProps.selectedVehicle?.activeMissionId?.value?.data ===
        nextProps.selectedVehicle?.activeMissionId?.value?.data &&
    prevProps.selectedVehicle?.vin === nextProps.selectedVehicle?.vin;

export default memo(Map, arePropsEqual);
